import { useState } from 'react'
import { Link } from 'react-router-dom'
import { TrashIcon } from '../components/Icons'
import { useAuth } from '../auth'

/**
 * The account-deletion page, at the public URL `/delete-account`.
 *
 * Google Play requires any app offering sign-up to publish a page where deletion can be requested
 * without first opening the app, and to list that URL on the store entry. It therefore has to
 * render for someone who is signed out — hence its place in the signed-out routes — and explain
 * what deletion does before asking anyone to confirm it.
 */
export function DeleteAccountPage() {
  const { user, deleteAccount } = useAuth()
  const [password, setPassword] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await deleteAccount(password)
      setDone(true)
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Something went wrong')
    } finally {
      setBusy(false)
    }
  }

  if (done) {
    return (
      <div className="page page-narrow">
        <h1>Your account has been deleted</h1>
        <div className="card form-card">
          <p className="muted" style={{ marginTop: 0 }}>
            The account and every activity recorded on it have been removed. Nothing is kept, so
            there is nothing left to restore.
          </p>
          <p className="muted">
            You are welcome back any time — signing up again with the same email starts fresh.
          </p>
          <Link className="link" to="/sign-in">
            Back to sign in
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="page page-narrow">
      <div className="page-head">
        <div>
          <Link to="/" className="muted">
            ← Back
          </Link>
          <h1 style={{ marginTop: 6 }}>Delete your account</h1>
        </div>
      </div>

      <div className="card form-card">
        <p className="muted" style={{ marginTop: 0 }}>
          Deleting your My Tracker account permanently removes:
        </p>
        <ul className="muted" style={{ marginTop: 8, paddingLeft: 20, lineHeight: 1.7 }}>
          <li>your account, name, email and password</li>
          <li>your height, weight, date of birth and gender</li>
          <li>every activity you have uploaded, including each original .tcx file</li>
          <li>all GPS positions, heart-rate readings and lap data within them</li>
        </ul>
        <p className="muted">
          This happens immediately and cannot be undone. There is no grace period and no backup
          copy to restore from. Activities still on your phone stay there until you uninstall the
          app or delete them.
        </p>
        <p className="muted">
          <strong>Download anything you want to keep first.</strong> Each activity has a{' '}
          <em>Download TCX</em> button that gives you the original file, which imports into Garmin
          Connect, Strava and anything else that reads TCX.
        </p>

        {user ? (
          <form onSubmit={submit} style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
            <p className="muted" style={{ marginTop: 0 }}>
              Signed in as <strong>{user.email}</strong>.
            </p>

            <label htmlFor="password">Confirm your password</label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />

            <label
              htmlFor="understand"
              style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 14 }}
            >
              <input
                id="understand"
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                style={{ width: 'auto', marginTop: 2 }}
              />
              <span style={{ fontWeight: 400 }}>
                I understand this deletes my account and all my activities permanently.
              </span>
            </label>

            {error && <div className="error">{error}</div>}

            <button className="danger" type="submit" disabled={busy || !confirmed || !password}>
              <TrashIcon />
              {busy ? 'Deleting…' : 'Delete my account'}
            </button>
          </form>
        ) : (
          <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
            <p className="muted" style={{ marginTop: 0 }}>
              Sign in with the account you want to delete, then come back to this page. Deletion is
              confirmed with your password, so nobody else can request it on your behalf.
            </p>
            <Link className="download-button" to="/sign-in" style={{ marginTop: 4 }}>
              Sign in to continue
            </Link>
            <p className="muted" style={{ fontSize: 13, marginTop: 14 }}>
              Cannot sign in? Reset your password first, or email{' '}
              <strong>support@mytracker.fitness</strong> from the address on the account and we
              will delete it for you.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
